/**
 * Lottie does not support dynamically switching a 2D context after initialization.
 * This proxy wraps a CanvasRenderingContext2D so that Lottie holds a stable reference
 * while the underlying context can be swapped to a different (reusable) canvas each frame.
 *
 * Method wrappers are cached per property key and lazily re-created after each
 * {@link switchContext} call. Property reads/writes are forwarded directly.
 */
export class SwitchableContextProxy {
    #currentContext: CanvasRenderingContext2D;
    #cachedMethods = new Map<string | symbol, (...args: unknown[]) => unknown>();
    #proxy: CanvasRenderingContext2D;

    // last fillStyle/strokeStyle which lottie explicitly set via set
    #lastFillStyle: string | CanvasGradient | CanvasPattern = '#000000';
    #lastStrokeStyle: string | CanvasGradient | CanvasPattern = '#000000';

    constructor(initialContext: CanvasRenderingContext2D) {
        this.#currentContext = initialContext;

        this.#proxy = new Proxy<CanvasRenderingContext2D>({} as CanvasRenderingContext2D, {
            get: <K extends keyof CanvasRenderingContext2D>(
                _target: CanvasRenderingContext2D,
                propKey: K,
            ) => {
                // We intercept fill operations - we forcibly apply styles before drawing.
                // This solves the problem with glyphs: lottie sets fillStyle once for the whole
                // text layer and does not reset it for each character after restore().
                // Canvas resets fillStyle on restore(), but we remember what lottie set last time.
                if (propKey === 'fill') {
                    return (...args: unknown[]) => {
                        this.#currentContext.fillStyle = this.#lastFillStyle;
                        return this.#currentContext.fill(
                            ...(args as Parameters<CanvasRenderingContext2D['fill']>),
                        );
                    };
                }

                if (propKey === 'fillRect') {
                    return (...args: unknown[]) => {
                        this.#currentContext.fillStyle = this.#lastFillStyle;
                        return this.#currentContext.fillRect(
                            ...(args as Parameters<CanvasRenderingContext2D['fillRect']>),
                        );
                    };
                }

                if (propKey === 'fillText') {
                    return (...args: unknown[]) => {
                        this.#currentContext.fillStyle = this.#lastFillStyle;
                        return this.#currentContext.fillText(
                            ...(args as Parameters<CanvasRenderingContext2D['fillText']>),
                        );
                    };
                }

                if (propKey === 'stroke') {
                    return (...args: unknown[]) => {
                        this.#currentContext.strokeStyle = this.#lastStrokeStyle;
                        return this.#currentContext.stroke(
                            ...(args as Parameters<CanvasRenderingContext2D['stroke']>),
                        );
                    };
                }

                if (propKey === 'strokeText') {
                    return (...args: unknown[]) => {
                        this.#currentContext.strokeStyle = this.#lastStrokeStyle;
                        return this.#currentContext.strokeText(
                            ...(args as Parameters<CanvasRenderingContext2D['strokeText']>),
                        );
                    };
                }

                const cached = this.#cachedMethods.get(propKey);
                if (cached) return cached;

                const value = this.#currentContext[propKey];
                if (typeof value === 'function') {
                    const bound = (...args: unknown[]) =>
                        (this.#currentContext[propKey] as Function).apply(
                            this.#currentContext,
                            args,
                        );
                    this.#cachedMethods.set(propKey, bound);
                    return bound;
                }
                return value;
            },

            set: <K extends keyof CanvasRenderingContext2D>(
                _target: CanvasRenderingContext2D,
                propKey: K,
                value: CanvasRenderingContext2D[K],
            ) => {
                // We remember what lottie explicitly set - regardless of restore()
                if (propKey === 'fillStyle') {
                    this.#lastFillStyle = value as string | CanvasGradient | CanvasPattern;
                }
                if (propKey === 'strokeStyle') {
                    this.#lastStrokeStyle = value as string | CanvasGradient | CanvasPattern;
                }
                this.#currentContext[propKey] = value;
                return true;
            },
        });
    }

    switchContext(newContext: CanvasRenderingContext2D) {
        if (this.#currentContext === newContext) return;
        this.#currentContext = newContext;
        this.#cachedMethods.clear();
        // When we switch context, we need to make sure that styles are consistent with what lottie set last time
        this.#currentContext.fillStyle = this.#lastFillStyle;
        this.#currentContext.strokeStyle = this.#lastStrokeStyle;
    }

    getProxy(): CanvasRenderingContext2D {
        return this.#proxy;
    }
}

interface FakeContextState {
    _fillStyle: string | CanvasGradient | CanvasPattern;
    _strokeStyle: string | CanvasGradient | CanvasPattern;
    _globalAlpha: number;
    _globalCompositeOperation: GlobalCompositeOperation;
    _lineWidth: number;
    _lineCap: CanvasLineCap;
    _lineJoin: CanvasLineJoin;
    _miterLimit: number;
    _lineDashOffset: number;
    _shadowOffsetX: number;
    _shadowOffsetY: number;
    _shadowBlur: number;
    _shadowColor: string;
    _font: string;
    _textAlign: CanvasTextAlign;
    _textBaseline: CanvasTextBaseline;
    _direction: CanvasDirection;
    _imageSmoothingEnabled: boolean;
    _imageSmoothingQuality: ImageSmoothingQuality;
    _filter: string;
    _stack: FakeContextState[];
}

/**
 * We can't allocate canvas for each lottie instance, because it can be very expensive,
 * so we use shared textures and switch context for lottie when needed.
 */
export function createFakeContext(width: number, height: number) {
    const ctx: FakeContextState = {
        _stack: [],
        _fillStyle: '',
        _strokeStyle: '',
        _globalAlpha: 1,
        _globalCompositeOperation: 'source-over',
        _lineWidth: 1,
        _lineCap: 'butt',
        _lineJoin: 'miter',
        _miterLimit: 10,
        _lineDashOffset: 0,
        _shadowOffsetX: 0,
        _shadowOffsetY: 0,
        _shadowBlur: 0,
        _shadowColor: '',
        _font: '10px sans-serif',
        _textAlign: 'start',
        _textBaseline: 'alphabetic',
        _direction: 'inherit',
        _imageSmoothingEnabled: true,
        _imageSmoothingQuality: 'low',
        _filter: 'none',
    };

    return {
        canvas: {
            width,
            height,
            fakeCanvas: true,
        },

        save() {
            ctx._stack.push({ ...ctx });
        },
        restore() {
            const state = ctx._stack.pop();
            if (state) Object.assign(ctx, state);
        },

        beginPath() {},
        closePath() {},
        clip() {},
        rect() {},
        clearRect() {},
        fillRect() {},
        strokeRect() {},
        moveTo() {},
        lineTo() {},
        bezierCurveTo() {},
        quadraticCurveTo() {},
        arc() {},
        arcTo() {},
        ellipse() {},
        transform() {},
        setTransform() {},
        resetTransform() {},
        scale() {},
        rotate() {},
        translate() {},
        drawImage() {},
        stroke() {},
        fill() {},
        fillText() {},
        strokeText() {},
        setLineDash() {},
        getLineDash() {
            return [];
        },
        createLinearGradient() {
            return {} as CanvasGradient;
        },
        createRadialGradient() {
            return {} as CanvasGradient;
        },
        createConicGradient() {
            return {} as CanvasGradient;
        },
        createPattern() {
            return null;
        },
        getTransform() {
            return new DOMMatrix();
        },
        measureText() {
            return { width: 0 } as TextMetrics;
        },
        isPointInPath() {
            return false;
        },
        isPointInStroke() {
            return false;
        },
        createImageData() {
            return {} as ImageData;
        },
        getImageData() {
            return {} as ImageData;
        },
        putImageData() {},
        drawFocusIfNeeded() {},

        get fillStyle() {
            return ctx._fillStyle;
        },
        set fillStyle(v) {
            ctx._fillStyle = v;
        },

        get strokeStyle() {
            return ctx._strokeStyle;
        },
        set strokeStyle(v) {
            ctx._strokeStyle = v;
        },

        get globalAlpha() {
            return ctx._globalAlpha;
        },
        set globalAlpha(v) {
            ctx._globalAlpha = v;
        },

        get globalCompositeOperation() {
            return ctx._globalCompositeOperation;
        },
        set globalCompositeOperation(v) {
            ctx._globalCompositeOperation = v;
        },

        get lineWidth() {
            return ctx._lineWidth;
        },
        set lineWidth(v) {
            ctx._lineWidth = v;
        },

        get lineCap() {
            return ctx._lineCap;
        },
        set lineCap(v) {
            ctx._lineCap = v;
        },

        get lineJoin() {
            return ctx._lineJoin;
        },
        set lineJoin(v) {
            ctx._lineJoin = v;
        },

        get miterLimit() {
            return ctx._miterLimit;
        },
        set miterLimit(v) {
            ctx._miterLimit = v;
        },

        get lineDashOffset() {
            return ctx._lineDashOffset;
        },
        set lineDashOffset(v) {
            ctx._lineDashOffset = v;
        },

        get shadowOffsetX() {
            return ctx._shadowOffsetX;
        },
        set shadowOffsetX(v) {
            ctx._shadowOffsetX = v;
        },

        get shadowOffsetY() {
            return ctx._shadowOffsetY;
        },
        set shadowOffsetY(v) {
            ctx._shadowOffsetY = v;
        },

        get shadowBlur() {
            return ctx._shadowBlur;
        },
        set shadowBlur(v) {
            ctx._shadowBlur = v;
        },

        get shadowColor() {
            return ctx._shadowColor;
        },
        set shadowColor(v) {
            ctx._shadowColor = v;
        },

        get font() {
            return ctx._font;
        },
        set font(v) {
            ctx._font = v;
        },

        get textAlign() {
            return ctx._textAlign;
        },
        set textAlign(v) {
            ctx._textAlign = v;
        },

        get textBaseline() {
            return ctx._textBaseline;
        },
        set textBaseline(v) {
            ctx._textBaseline = v;
        },

        get direction() {
            return ctx._direction;
        },
        set direction(v) {
            ctx._direction = v;
        },

        get imageSmoothingEnabled() {
            return ctx._imageSmoothingEnabled;
        },
        set imageSmoothingEnabled(v) {
            ctx._imageSmoothingEnabled = v;
        },

        get imageSmoothingQuality() {
            return ctx._imageSmoothingQuality;
        },
        set imageSmoothingQuality(v) {
            ctx._imageSmoothingQuality = v;
        },

        get filter() {
            return ctx._filter;
        },
        set filter(v) {
            ctx._filter = v;
        },
    } as unknown as CanvasRenderingContext2D;
}
